import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Plus, Edit, Trash2, CreditCard, Building2, User } from "lucide-react";

const PAYMENT_TYPES = [
  { value: "cash", label: "Cash" },
  { value: "check", label: "Check" },
  { value: "card", label: "Credit/Debit Card" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "financing", label: "Financing" },
  { value: "other", label: "Other" },
];

/**
 * PaymentTypesManager Component
 * Manages payment types CRUD operations
 */
const PaymentTypesManager = ({
  paymentTypes,
  paymentDialogOpen,
  setPaymentDialogOpen,
  editingPayment,
  paymentForm,
  setPaymentForm,
  resetPaymentForm,
  editPayment,
  savePaymentType,
  togglePaymentActive,
  confirmDelete,
}) => {
  return (
    <Card className="glass-card border-theme" data-testid="payment-types-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-foreground font-['Manrope']">Payment Methods</CardTitle>
          <CardDescription className="text-muted-foreground">
            Configure accepted payment methods and processing fees
          </CardDescription>
        </div>
        <Dialog open={paymentDialogOpen} onOpenChange={(open) => { setPaymentDialogOpen(open); if (!open) resetPaymentForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-payment-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Method
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-background border-border max-w-md">
            <DialogHeader>
              <DialogTitle className="text-foreground">{editingPayment ? "Edit" : "Add"} Payment Method</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Name *</Label>
                  <Input
                    value={paymentForm.name}
                    onChange={(e) => setPaymentForm({ ...paymentForm, name: e.target.value })}
                    className="input-glass"
                    placeholder="e.g., Visa/Mastercard"
                    data-testid="payment-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Type</Label>
                  <Select value={paymentForm.type} onValueChange={(v) => setPaymentForm({ ...paymentForm, type: v })}>
                    <SelectTrigger className="input-glass"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAYMENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Processing Fee (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={paymentForm.processing_fee}
                  onChange={(e) => setPaymentForm({ ...paymentForm, processing_fee: e.target.value })}
                  className="input-glass"
                  placeholder="0"
                  data-testid="payment-fee-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Default Collected By</Label>
                <Select value={paymentForm.default_collected_by || "company"} onValueChange={(v) => setPaymentForm({ ...paymentForm, default_collected_by: v })}>
                  <SelectTrigger className="input-glass" data-testid="payment-collected-by-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="company">
                      <span className="flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> Company</span>
                    </SelectItem>
                    <SelectItem value="technician">
                      <span className="flex items-center gap-2"><User className="w-3.5 h-3.5" /> Technician</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Instructions for Invoices</Label>
                <Textarea
                  value={paymentForm.instructions}
                  onChange={(e) => setPaymentForm({ ...paymentForm, instructions: e.target.value })}
                  className="input-glass min-h-[60px]"
                  placeholder="e.g., Make checks payable to..."
                />
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={paymentForm.is_default}
                  onCheckedChange={(checked) => setPaymentForm({ ...paymentForm, is_default: checked })}
                  data-testid="payment-default-switch"
                />
                <Label className="text-muted-foreground">Set as default payment method</Label>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="ghost" onClick={() => setPaymentDialogOpen(false)} className="text-muted-foreground">Cancel</Button>
                <Button onClick={savePaymentType} className="btn-primary" data-testid="save-payment-btn">
                  {editingPayment ? "Update" : "Create"} Method
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {paymentTypes.length > 0 ? (
          <div className="space-y-3">
            {paymentTypes.map((payment, i) => (
              <div key={payment.payment_id} className="flex items-center justify-between p-4 rounded-xl bg-theme-surface border border-theme" data-testid={`payment-item-${i}`}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#FFCC00]/20 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-[#FFCC00]" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-foreground">{payment.name}</h4>
                      {payment.is_default && (
                        <Badge className="bg-[#FFCC00]/20 text-[#FFCC00] text-xs">Default</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <Badge variant="outline" className="text-xs capitalize">{payment.type}</Badge>
                      {payment.processing_fee > 0 && (
                        <span>{payment.processing_fee}% fee</span>
                      )}
                      <span className="flex items-center gap-1 text-xs">
                        {payment.default_collected_by === "technician" ? (
                          <><User className="w-3 h-3" /> Tech collects</>
                        ) : (
                          <><Building2 className="w-3 h-3" /> Company collects</>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={payment.is_active}
                    onCheckedChange={(checked) => togglePaymentActive(payment.payment_id, checked)}
                  />
                  <Button variant="ghost" size="icon" onClick={() => editPayment(payment)} className="h-8 w-8 text-muted-foreground hover:text-foreground">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => confirmDelete("payment", payment.payment_id, payment.name)} className="h-8 w-8 text-red-400 hover:text-red-300">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <CreditCard className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">No payment methods configured</p>
            <p className="text-muted-foreground/60 text-sm mt-1">Payment methods will be auto-created on first load</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PaymentTypesManager;
